import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const DemoSignup = () => {
  const navigate = useNavigate();
  const { user, signIn, signUp } = useAuth();
  const [mode, setMode] = useState('signup'); // 'signup' or 'login'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      // User is logged in, redirect to complete the analysis
      const pending = localStorage.getItem('pendingAnalysis');
      if (pending) {
        navigate('/property-analysis-wizard?fromDemo=true');
      } else {
        navigate('/main-dashboard');
      }
    }
  }, [user, navigate]);

  // Load pending analysis data
  useEffect(() => {
    const pending = localStorage.getItem('pendingAnalysis');
    if (pending) {
      try {
        setPendingAnalysis(JSON.parse(pending));
      } catch (e) {
        console.error('Error parsing pending analysis:', e);
      }
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'L\'adresse courriel est requise';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = 'Veuillez entrer une adresse courriel valide';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (mode === 'signup' && formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (mode === 'signup') {
        const { error } = await signUp(formData?.email, formData?.password);

        if (error) {
          let errorMessage = 'Erreur lors de la création du compte';
          if (error?.message?.includes('already registered')) {
            errorMessage = 'Cette adresse courriel est déjà utilisée. Connectez-vous plutôt.';
          } else if (error?.message) {
            errorMessage = error?.message;
          }
          setErrors({ general: errorMessage });
          setIsLoading(false);
          return;
        }

        // Account created, redirect to wizard
        setTimeout(() => {
          navigate('/property-analysis-wizard?fromDemo=true');
        }, 500);
      } else {
        const { error } = await signIn(formData?.email, formData?.password);

        if (error) {
          let errorMessage = 'Adresse courriel ou mot de passe incorrect';
          if (error?.message?.includes('Email not confirmed')) {
            errorMessage = 'Veuillez confirmer votre adresse courriel avant de vous connecter.';
          } else if (error?.message) {
            errorMessage = error?.message;
          }
          setErrors({ general: errorMessage });
          setIsLoading(false);
          return;
        }

        // Login successful, redirect to wizard
        setTimeout(() => {
          navigate('/property-analysis-wizard?fromDemo=true');
        }, 500);
      }
    } catch (error) {
      setErrors({ general: 'Erreur réseau. Veuillez réessayer.' });
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    try {
      const { error } = await supabase?.auth?.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location?.origin}/property-analysis-wizard?fromDemo=true`
        }
      });

      if (error) {
        setErrors({
          general: `Erreur lors de la connexion avec ${provider}. Veuillez réessayer.`
        });
        setIsLoading(false);
      }
    } catch (error) {
      setErrors({ general: 'Erreur réseau. Veuillez réessayer.' });
      setIsLoading(false);
    }
  };

  const propertyAddress = pendingAnalysis?.address || 'Votre propriété';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-background)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--spacing-md)'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '480px'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <h1 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.75rem',
              fontWeight: 700,
              color: 'var(--color-primary)',
              margin: 0
            }}>
              ImmoIQ
            </h1>
          </Link>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--color-card)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--color-border)',
          padding: 'var(--spacing-xl)',
          boxShadow: 'var(--shadow-lg)'
        }}>
          {/* Header with icon */}
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--color-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--spacing-md)'
            }}>
              <Icon name="Lock" size={32} color="white" />
            </div>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: '0 0 var(--spacing-sm)'
            }}>
              Sauvegardez votre analyse
            </h2>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.9375rem',
              color: 'var(--color-muted-foreground)',
              margin: 0,
              lineHeight: 1.6
            }}>
              Créez un compte gratuit pour sauvegarder et consulter l'analyse détaillée de votre propriété.
            </p>
          </div>

          {/* Property summary */}
          {pendingAnalysis && (
            <div style={{
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-lg)',
              border: '1px solid var(--color-border)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                <Icon name="Building2" size={20} color="var(--color-primary)" />
                <div>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--color-foreground)',
                    margin: 0
                  }}>
                    {propertyAddress}
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    color: 'var(--color-muted-foreground)',
                    margin: 0
                  }}>
                    Analyse prête à être sauvegardée
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mode toggle */}
          <div style={{
            display: 'flex',
            marginBottom: 'var(--spacing-lg)',
            background: 'var(--color-muted)',
            borderRadius: 'var(--radius-md)',
            padding: '4px'
          }}>
            <button
              onClick={() => setMode('signup')}
              style={{
                flex: 1,
                padding: 'var(--spacing-sm)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                background: mode === 'signup' ? 'var(--color-card)' : 'transparent',
                color: mode === 'signup' ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: mode === 'signup' ? 'var(--shadow-sm)' : 'none'
              }}
            >
              Créer un compte
            </button>
            <button
              onClick={() => setMode('login')}
              style={{
                flex: 1,
                padding: 'var(--spacing-sm)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                background: mode === 'login' ? 'var(--color-card)' : 'transparent',
                color: mode === 'login' ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: mode === 'login' ? 'var(--shadow-sm)' : 'none'
              }}
            >
              Se connecter
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {errors?.general && (
              <div style={{
                padding: 'var(--spacing-sm)',
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--spacing-md)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-xs)'
              }}>
                <Icon name="AlertCircle" size={18} color="var(--color-error)" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-error)',
                  margin: 0
                }}>
                  {errors?.general}
                </p>
              </div>
            )}

            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <Input
                type="email"
                name="email"
                label="Adresse courriel"
                placeholder="votre.email@exemple.ca"
                value={formData?.email}
                onChange={handleInputChange}
                error={errors?.email}
                required
                disabled={isLoading}
              />
            </div>

            <div style={{ marginBottom: 'var(--spacing-md)', position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Mot de passe"
                placeholder={mode === 'signup' ? 'Minimum 8 caractères' : 'Entrez votre mot de passe'}
                value={formData?.password}
                onChange={handleInputChange}
                error={errors?.password}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '38px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--color-muted-foreground)'
                }}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            {mode === 'signup' && (
              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <Input
                  type="password"
                  name="confirmPassword"
                  label="Confirmer le mot de passe"
                  placeholder="Confirmez votre mot de passe"
                  value={formData?.confirmPassword}
                  onChange={handleInputChange}
                  error={errors?.confirmPassword}
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              iconName={mode === 'signup' ? 'UserPlus' : 'LogIn'}
              iconPosition="right"
            >
              {mode === 'signup' ? 'Créer mon compte' : 'Se connecter'}
            </Button>
          </form>

          {/* Divider */}
          <div style={{
            position: 'relative',
            margin: 'var(--spacing-lg) 0',
            textAlign: 'center'
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1px',
              background: 'var(--color-border)'
            }} />
            <span style={{
              position: 'relative',
              display: 'inline-block',
              padding: '0 var(--spacing-sm)',
              background: 'var(--color-card)',
              fontFamily: 'var(--font-caption)',
              fontSize: '0.8125rem',
              color: 'var(--color-muted-foreground)'
            }}>
              Ou continuer avec
            </span>
          </div>

          {/* Social login */}
          <div>
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={() => handleSocialLogin('google')}
              disabled={isLoading}
              iconName="Mail"
              iconPosition="left"
              fullWidth
            >
              Continuer avec Google
            </Button>
          </div>
        </div>

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-muted-foreground)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)'
            }}
          >
            <Icon name="ArrowLeft" size={16} />
            Retour à l'analyse
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoSignup;
